<template>
  <div>
    <div class="trimmer-overlay" v-show="visible" @click="handleCancel">
      <!-- 漂浮窗主体 -->
      <div class="trimmer-modal" @click.stop>
        <!-- 关闭按钮 -->
        <div class="trimmer-close-btn" @click="handleCancel">&times;</div>
        <!-- 漂浮窗内容 -->
        <div class="trimmer-modal-body">
          <!-- 视频预览区域 -->
          <div class="trimmer-preview" @click="togglePlay">
            <video
              ref="videoPlayer"
              class="preview-video"
              :src="videoSrc"
              preload="auto"
              playsinline
              controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
              disablePictureInPicture
              @loadedmetadata="onVideoLoaded"
              @timeupdate="onTimeUpdate"
              @seeked="onSeeked"
            ></video>
            <div class="preview-time">
              {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
            </div>
          </div>

          <!-- 时间轴区域 -->
          <div class="trimmer-timeline">
            <!-- 帧缩略图 + 裁剪选择 合一区域 -->
            <div class="timeline-track" ref="timelineTrack" @mousedown="onTrackMouseDown">
              <!-- 帧缩略图背景（单张canvas铺满） -->
              <canvas ref="timelineCanvas" class="timeline-filmstrip"></canvas>

              <!-- 左侧非选中区域遮罩 -->
              <div class="track-mask left" :style="{ width: leftHandlePercent + '%' }"></div>

              <!-- 选中区域（可拖动整体） -->
              <div
                class="track-range"
                :style="{ left: leftHandlePercent + '%', width: selectedRangePercent + '%' }"
                @mousedown.stop="onRangeMouseDown"
              ></div>

              <!-- 左侧滑块 -->
              <div
                class="track-handle left"
                :style="{ left: leftHandlePercent + '%' }"
                @mousedown.stop="onLeftHandleMouseDown"
              >
                <div class="handle-line"></div>
                <div class="handle-grip"></div>
                <div class="handle-time">{{ formatTime(startTime) }}</div>
              </div>

              <!-- 右侧滑块 -->
              <div
                class="track-handle right"
                :style="{ left: (leftHandlePercent + selectedRangePercent) + '%' }"
                @mousedown.stop="onRightHandleMouseDown"
              >
                <div class="handle-line"></div>
                <div class="handle-grip"></div>
                <div class="handle-time">{{ formatTime(endTime) }}</div>
              </div>

              <!-- 右侧非选中区域遮罩 -->
              <div class="track-mask right" :style="{ left: (leftHandlePercent + selectedRangePercent) + '%', width: (100 - leftHandlePercent - selectedRangePercent) + '%' }"></div>

              <!-- 播放头指示器 -->
              <div class="playhead" :style="{ left: playheadPercent + '%' }">
                <div class="playhead-line"></div>
                <div class="playhead-arrow"></div>
              </div>
            </div>

            <!-- 时间刻度 -->
            <div class="timeline-ticks">
              <div v-for="tick in timelineTicks" :key="tick.index" class="tick" :style="{ left: tick.percent + '%' }">
                <span class="tick-mark"></span>
                <span class="tick-label">{{ formatTime(tick.time) }}</span>
              </div>
            </div>
          </div>

          <!-- 底部按钮 -->
          <div class="trimmer-footer">
            <div class="selected-duration">
              选中时长: <span class="duration-value">{{ formatTime(endTime - startTime) }}</span>
            </div>
            <div class="trimmer-actions">
              <button class="trimmer-btn save-btn" @click="handleSave">保存</button>
              <button class="trimmer-btn reset-btn" @click="handleReset">重置</button>
              <button class="trimmer-btn cancel-btn" @click="handleCancel">取消</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoTrimmerDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    videoSrc: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      duration: 0,
      currentTime: 0,
      startTime: 0,
      endTime: 0,
      frameDataList: [],
      isDragging: false,
      dragType: null,
      dragStartX: 0,
      dragStartLeft: 0,
      dragStartRange: 0,
      isSeeking: false,
      framesCaptured: false
    }
  },
  computed: {
    leftHandlePercent() {
      if (this.duration <= 0) return 0
      return (this.startTime / this.duration) * 100
    },
    selectedRangePercent() {
      if (this.duration <= 0) return 100
      return ((this.endTime - this.startTime) / this.duration) * 100
    },
    playheadPercent() {
      if (this.duration <= 0) return 0
      return (this.currentTime / this.duration) * 100
    },
    timelineTicks() {
      const ticks = []
      if (this.duration <= 0) return ticks
      const tickCount = Math.min(10, Math.max(4, Math.floor(this.duration)))
      const interval = this.duration / tickCount
      for (let i = 0; i <= tickCount; i++) {
        ticks.push({
          index: i,
          time: i * interval,
          percent: (i / tickCount) * 100
        })
      }
      return ticks
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.$nextTick(() => {
          this.resetState()
          this.initVideoOnReopen()
        })
      } else {
        this.stopVideo()
      }
    },
    videoSrc() {
      if (this.visible) {
        this.$nextTick(() => {
          this.resetState()
          this.initVideoOnReopen()
        })
      }
    }
  },
  mounted() {
    document.addEventListener('mousemove', this.onMouseMove)
    document.addEventListener('mouseup', this.onMouseUp)
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onMouseMove)
    document.removeEventListener('mouseup', this.onMouseUp)
    window.removeEventListener('keydown', this.handleKeydown)
    this.stopVideo()
  },
  methods: {
    resetState() {
      this.duration = 0
      this.currentTime = 0
      this.startTime = 0
      this.endTime = 0
      this.frameDataList = []
      this.isDragging = false
      this.dragType = null
      this.framesCaptured = false
    },
    // 重新打开时检查视频元数据是否已加载（v-show不销毁DOM，loadedmetadata不会再次触发）
    initVideoOnReopen() {
      const video = this.$refs.videoPlayer
      if (!video) return
      if (video.readyState >= 1 && video.duration > 0) {
        // 元数据已缓存，直接初始化
        this.duration = video.duration
        this.startTime = 0
        this.endTime = video.duration
        this.currentTime = 0
        video.currentTime = 0
        this.$nextTick(() => {
          this.generateFilmstrip()
        })
      }
      // 否则等待 loadedmetadata 事件
    },
    stopVideo() {
      const video = this.$refs.videoPlayer
      if (video) {
        video.pause()
      }
    },
    handleKeydown(event) {
      if (event.key === 'Escape' && this.visible) {
        this.handleCancel()
      }
    },
    onVideoLoaded() {
      const video = this.$refs.videoPlayer
      if (video) {
        this.duration = video.duration
        this.startTime = 0
        this.endTime = video.duration
        this.currentTime = 0
        this.generateFilmstrip()
      }
    },
    onTimeUpdate() {
      if (this.isSeeking) return
      const video = this.$refs.videoPlayer
      if (video && !this.isDragging) {
        this.currentTime = video.currentTime
      }
    },
    onSeeked() {
      this.isSeeking = false
    },
    // 生成帧缩略图并绘制到单张canvas上
    async generateFilmstrip() {
      const video = this.$refs.videoPlayer
      const canvas = this.$refs.timelineCanvas
      if (!video || !canvas || this.duration <= 0) return

      // 获取时间轴区域的实际尺寸
      const track = this.$refs.timelineTrack
      if (!track) return
      const trackRect = track.getBoundingClientRect()
      const canvasWidth = Math.floor(trackRect.width)
      const canvasHeight = Math.floor(trackRect.height)

      canvas.width = canvasWidth
      canvas.height = canvasHeight
      canvas.style.width = canvasWidth + 'px'
      canvas.style.height = canvasHeight + 'px'

      const ctx = canvas.getContext('2d')
      const frameCount = 20
      const frameWidth = canvasWidth / frameCount
      const interval = this.duration / frameCount

      this.frameDataList = []

      for (let i = 0; i < frameCount; i++) {
        try {
          video.currentTime = i * interval + interval * 0.5
          await new Promise((resolve) => {
            video.onseeked = resolve
          })
          // 按比例裁切绘制，避免拉伸变形
          const vw = video.videoWidth
          const vh = video.videoHeight
          if (vw && vh) {
            const videoAspect = vw / vh
            const frameAspect = frameWidth / canvasHeight
            let sx = 0
            let sy = 0
            let sw = vw
            let sh = vh
            if (videoAspect > frameAspect) {
              sw = vh * frameAspect
              sx = (vw - sw) / 2
            } else {
              sh = vw / frameAspect
              sy = (vh - sh) / 2
            }
            ctx.drawImage(video, sx, sy, sw, sh, i * frameWidth, 0, frameWidth, canvasHeight)
          } else {
            ctx.drawImage(video, i * frameWidth, 0, frameWidth, canvasHeight)
          }
          this.frameDataList.push({ time: i * interval, captured: true })
        } catch (e) {
          console.warn('捕获帧失败:', i, e)
        }
      }

      // 绘制帧分隔线
      ctx.strokeStyle = 'rgba(255,255,255,0.15)'
      ctx.lineWidth = 1
      for (let i = 1; i < frameCount; i++) {
        const x = i * frameWidth
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, canvasHeight)
        ctx.stroke()
      }

      this.framesCaptured = true
      // 恢复到开始位置
      video.currentTime = 0
    },
    // 格式化时间
    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '00:00.0'
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      const ms = Math.floor((seconds % 1) * 10)
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${ms}`
    },
    // 滑块拖拽处理
    onLeftHandleMouseDown(e) {
      this.isDragging = true
      this.dragType = 'left'
      this.dragStartX = e.clientX
      this.dragStartLeft = this.startTime
      e.preventDefault()
    },
    onRightHandleMouseDown(e) {
      this.isDragging = true
      this.dragType = 'right'
      this.dragStartX = e.clientX
      this.dragStartLeft = this.endTime
      e.preventDefault()
    },
    onRangeMouseDown(e) {
      this.isDragging = true
      this.dragType = 'range'
      this.dragStartX = e.clientX
      this.dragStartLeft = this.startTime
      this.dragStartRange = this.endTime - this.startTime
      e.preventDefault()
    },
    onTrackMouseDown(e) {
      if (e.target === this.$refs.timelineTrack || e.target.classList.contains('track-mask')) {
        const rect = this.$refs.timelineTrack.getBoundingClientRect()
        const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
        const time = percent * this.duration
        this.seekTo(time)
      }
    },
    onMouseMove(e) {
      if (!this.isDragging || !this.$refs.timelineTrack) return
      const rect = this.$refs.timelineTrack.getBoundingClientRect()
      const deltaX = e.clientX - this.dragStartX
      const deltaPercent = deltaX / rect.width
      const deltaTime = deltaPercent * this.duration

      if (this.dragType === 'left') {
        const newStart = Math.max(0, Math.min(this.dragStartLeft + deltaTime, this.endTime - 0.5))
        this.startTime = newStart
        this.seekTo(newStart)
      } else if (this.dragType === 'right') {
        const newEnd = Math.max(this.startTime + 0.5, Math.min(this.dragStartLeft + deltaTime, this.duration))
        this.endTime = newEnd
        this.seekTo(newEnd)
      } else if (this.dragType === 'range') {
        let newStart = this.dragStartLeft + deltaTime
        let newEnd = newStart + this.dragStartRange
        if (newStart < 0) {
          newStart = 0
          newEnd = this.dragStartRange
        } else if (newEnd > this.duration) {
          newEnd = this.duration
          newStart = newEnd - this.dragStartRange
        }
        this.startTime = newStart
        this.endTime = newEnd
        this.seekTo(newStart)
      }
    },
    onMouseUp() {
      if (this.isDragging) {
        this.isDragging = false
        this.dragType = null
      }
    },
    seekTo(time) {
      const video = this.$refs.videoPlayer
      if (video) {
        this.isSeeking = true
        video.currentTime = time
        this.currentTime = time
      }
    },
    togglePlay() {
      const video = this.$refs.videoPlayer
      if (!video) return
      if (video.paused) {
        video.play()
      } else {
        video.pause()
      }
    },
    handleSave() {
      const payload = {
        startTime: this.startTime,
        endTime: this.endTime,
        videoSrc: this.videoSrc
      }
      console.log('[VideoTrimmerDialog] 点击保存，emit save:', payload)
      this.$emit('save', payload)
    },
    handleReset() {
      this.startTime = 0
      this.endTime = this.duration
      this.seekTo(0)
    },
    handleCancel() {
      this.$emit('cancel')
    }
  }
}
</script>

<style lang="less" scoped>
@import "@/style/common.less";

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 遮罩层
.trimmer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

// 漂浮窗主体
.trimmer-modal {
  width: 1400px;
  max-width: 90vw;
  height: 850px;
  max-height: 90vh;
  background: white;
  border-radius: 30px;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fadeInUp 0.3s ease;
  position: relative;
  padding: 20px 30px 30px;
}

// 关闭按钮
.trimmer-close-btn {
  position: absolute;
  top: 20px;
  right: 30px;
  width: 40px;
  height: 40px;
  background: #f8f9fa;
  color: #6c757d;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 10;

  &:hover {
    background: #ff8c42;
    color: white;
    transform: scale(1.1);
  }
}

// 漂浮窗内容
.trimmer-modal-body {
  flex: 1;
  padding: 10px 0 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
}

// 视频预览区域
.trimmer-preview {
  flex: 1;
  background: #1a1a1a;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  min-height: 0;
  box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.15);

  .preview-video {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 12px;
    pointer-events: none;

    // 彻底隐藏浏览器原生播放控件
    &::-webkit-media-controls {
      display: none !important;
    }
    &::-webkit-media-controls-enclosure {
      display: none !important;
    }
    &::-webkit-media-controls-panel {
      display: none !important;
    }
    &::-webkit-media-controls-play-button {
      display: none !important;
    }
    &::-webkit-media-controls-start-playback-button {
      display: none !important;
    }
    &::-webkit-media-controls-overlay-play-button {
      display: none !important;
    }
    &::-webkit-media-controls-current-time-display {
      display: none !important;
    }
    &::-webkit-media-controls-timeline {
      display: none !important;
    }
    &::-webkit-media-controls-volume-slider {
      display: none !important;
    }
  }

  .preview-time {
    position: absolute;
    bottom: 16px;
    right: 20px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    font-family: monospace;
    background: rgba(0, 0, 0, 0.5);
    padding: 4px 12px;
    border-radius: 12px;
  }
}

// 时间轴区域
.trimmer-timeline {
  flex-shrink: 0;
  padding: 0 10px;
}

// 帧缩略图 + 裁剪选择 合一轨道
.timeline-track {
  position: relative;
  height: 100px;
  border-radius: 12px;
  cursor: crosshair;
  user-select: none;
  background: #2a2a2a;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);

  .timeline-filmstrip {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
  }

  .track-mask {
    position: absolute;
    top: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.55);
    pointer-events: none;
    z-index: 2;

    &.left {
      left: 0;
    }

    &.right {
      right: 0;
    }
  }

  .track-range {
    position: absolute;
    top: 0;
    bottom: 0;
    border-top: 3px solid @morejoy;
    border-bottom: 3px solid @morejoy;
    cursor: grab;
    z-index: 3;

    &:active {
      cursor: grabbing;
    }
  }

  .track-handle {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 14px;
    transform: translateX(-50%);
    cursor: ew-resize;
    z-index: 10;

    .handle-line {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 50%;
      width: 4px;
      background: @morejoy;
      transform: translateX(-50%);
      border-radius: 2px;
    }

    .handle-grip {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 18px;
      height: 40px;
      background: @morejoy;
      border-radius: 6px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

      &::before,
      &::after {
        content: '';
        position: absolute;
        left: 5px;
        right: 5px;
        height: 2px;
        background: white;
        border-radius: 1px;
      }

      &::before {
        top: 12px;
      }

      &::after {
        bottom: 12px;
      }
    }

    .handle-time {
      position: absolute;
      bottom: -28px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 12px;
      color: @morejoy;
      font-family: monospace;
      font-weight: 600;
      white-space: nowrap;
      background: white;
      padding: 3px 8px;
      border-radius: 6px;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }

    &.left .handle-time {
      transform: translateX(0);
    }

    &.right .handle-time {
      transform: translateX(-100%);
    }
  }

  .playhead {
    position: absolute;
    top: 0;
    bottom: 0;
    transform: translateX(-50%);
    pointer-events: none;
    z-index: 8;

    .playhead-line {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 50%;
      width: 2px;
      background: white;
      transform: translateX(-50%);
      box-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
    }

    .playhead-arrow {
      position: absolute;
      top: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 7px solid transparent;
      border-right: 7px solid transparent;
      border-top: 8px solid white;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
    }
  }
}

// 时间刻度
.timeline-ticks {
  position: relative;
  height: 30px;
  margin-top: 4px;

  .tick {
    position: absolute;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;

    .tick-mark {
      width: 1px;
      height: 6px;
      background: #ccc;
    }

    .tick-label {
      font-size: 11px;
      color: #999;
      font-family: monospace;
    }
  }
}

// 底部按钮
.trimmer-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0 10px;
  overflow: visible;

  .selected-duration {
    font-size: 16px;
    color: #666;
    font-weight: 500;

    .duration-value {
      color: @morejoy;
      font-family: monospace;
      font-weight: 600;
    }
  }

  .trimmer-actions {
    display: flex;
    gap: 20px;
  }

  .trimmer-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 40px;
    border: none;
    border-radius: 30px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

    &:active {
      transform: translateY(1px);
    }

    &.save-btn {
      background: #ff8c42;
      color: white;

      &:hover {
        background: #ff7a2e;
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(255, 140, 66, 0.4);
      }
    }

    &.reset-btn {
      background: white;
      color: #6c757d;
      border: 2px solid #dee2e6;

      &:hover {
        background: #f8f9fa;
        border-color: #adb5bd;
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
      }
    }

    &.cancel-btn {
      background: white;
      color: #6c757d;
      border: 2px solid #dee2e6;

      &:hover {
        background: #f8f9fa;
        border-color: #adb5bd;
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
      }
    }
  }
}
</style>
